/* eslint-disable react/require-default-props */
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { AccountClient } from '@nile/graphql';
import { useQuery } from '@apollo/client';
import {
  getTranslatedText,
  useIntl,
  Loader,
  useCurrencyCallback
} from '@nile/components';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';
import { GET_CLIENT_DEBT_INFO_LIST } from './invoiceGql';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

type DebtInfo = {
  client?: { name?: string };
  totalInvoicesOverdue?: number;
  outstandingAmount?: string | number;
};

type ClientDebtInfoListResponse = {
  getClientDebtInfoList?: {
    clientDebtInfos?: DebtInfo[];
  };
};

export const InvoiceStatementsChart = () => {
  const intl = useIntl();
  const { formatCurrency } = useCurrencyCallback();
  const [debts, setDebts] = useState<DebtInfo[]>([]);

  const { reportAllowedDateRange } = useInvoiceStatementsStore();

  const variables = useMemo(
    () => ({
      listInfoInput: reportAllowedDateRange
        ? {
            condition: {
              joinOperator: 'AND',
              operands: [reportAllowedDateRange]
            }
          }
        : {}
    }),
    [reportAllowedDateRange]
  );

  const { loading, refetch } = useQuery<ClientDebtInfoListResponse>(
    GET_CLIENT_DEBT_INFO_LIST,
    {
      client: AccountClient,
      variables,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const payload = data?.getClientDebtInfoList || {};
        const rows: DebtInfo[] = payload?.clientDebtInfos || [];
        const normalized = rows.map((r) => ({
          ...r,
          outstandingAmount:
            typeof r?.outstandingAmount === 'string'
              ? Number(r?.outstandingAmount)
              : Number(r?.outstandingAmount ?? 0)
        }));
        setDebts(normalized);
      }
    }
  );

  useEffect(() => {
    refetch(variables);
  }, [variables, refetch]);

  const option = useMemo(() => {
    const xLabels = debts.map((d) => d?.client?.name ?? '');
    const rawSeries = debts.map((d) => Number(d?.outstandingAmount ?? 0));
    const maxValue = Math.max(0, ...rawSeries);
    const positiveValues = rawSeries.filter((v) => v > 0);
    const minPositive = positiveValues.length ? Math.min(...positiveValues) : 0;
    // Enable log scale when there is a large spread so smaller values are visible
    const useLogScale =
      minPositive > 0 && maxValue / Math.max(minPositive, 1) >= 50;
    // ECharts log axis does not support zero; clamp non-positive values to 1 for visibility
    const seriesData = useLogScale
      ? rawSeries.map((v) => (v > 0 ? v : 1))
      : rawSeries;
    return {
      title: {
        text: getTranslatedText(
          intl,
          'invoice.statements.topDebtorsAnalysis.title'
        )
      },
      color: 'rgba(61, 79, 240, 1)',
      grid: {
        top: '20%'
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: { dataIndex: number }) => {
          const { dataIndex } = params || {};
          const row: DebtInfo | undefined = debts?.[dataIndex];
          if (!row) return '';
          const currencyText = formatCurrency(Number(row?.outstandingAmount));
          const overdueLabel = getTranslatedText(
            intl,
            'invoice.statements.tooltip.overdueInvoices'
          );
          const amountLabel = getTranslatedText(
            intl,
            'invoice.statements.tooltip.outstandingAmount'
          );
          return `<div>
              <div class="f-14 bold">${row?.client?.name ?? ''}</div>
              <ul class="tw-m-1 tw-pl-2">
                <li class="f-12">${overdueLabel} (${row?.totalInvoicesOverdue ??
            0})</li>
                <li class="f-12">${amountLabel}: <b>${currencyText}</b></li>
              </ul>
            </div>`;
        }
      },
      xAxis: {
        data: xLabels,
        type: 'category',
        axisLabel: {
          interval: 0,
          rotate: 30,
          margin: 30,
          fontSize: 12,
          ellipsis: true,
          overflow: 'breakAll',
          width: 60,
          align: 'center'
        }
      },
      yAxis: {
        type: useLogScale ? 'log' : 'value',
        min: useLogScale ? 1 : 0,
        logBase: 10,
        minorTick: useLogScale ? { show: true } : undefined,
        minorSplitLine: useLogScale ? { show: true } : undefined,
        // name: getTranslatedText(
        //   intl,
        //   'invoice.statements.axis.amountOutstanding'
        // ),
        nameLocation: 'end',
        nameTextStyle: {
          verticalAlign: 'bottom'
        },
        nameGap: 20
      },
      series: [
        {
          //   name: getTranslatedText(
          //     intl,
          //     'invoice.statements.series.outstandingAmount'
          //   ),
          type: 'bar',
          data: seriesData,
          large: true
        }
      ]
    };
  }, [debts, intl, formatCurrency]);

  if (loading) {
    return (
      <div className="flex-center full-height min-height-300">
        <Loader />
      </div>
    );
  }

  return (
    <div className="full-width full-height position-relative">
      <Suspense
        fallback={
          <div className="flex-center full-height min-height-300">
            <Loader />
          </div>
        }
      >
        <ReactECharts
          option={option}
          style={{ height: 400 }}
          opts={{ renderer: 'svg' }}
        />
      </Suspense>
    </div>
  );
};
